import {jwtDecode} from 'jwt-decode'
import {AuthModel, RefreshTokenResponseModel} from './_models'
import {
  AUTH_LOCAL_STORAGE_KEY,
  getAuth,
  removeAuth,
  setAuth,
  isAuthRoutePath,
} from './AuthHelpers'
import {getAuthSideEffects} from './authDataLayerBridge'
import {refreshToken as refreshTokenRequest} from './_requests'

let refreshPromise: Promise<AuthModel | null> | null = null
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null
let storageSyncInitialized = false

const DEFAULT_EXPIRY_BUFFER_SECONDS = 60

export const AUTH_SESSION_EXPIRED_EVENT = 'auth-session-expired'

const parsePolicies = (policies: string | undefined): number[] => {
  if (!policies) {
    return []
  }

  try {
    return JSON.parse(policies).map((p: any) => Number(p))
  } catch (error) {
    console.error('POLICIES PARSE ERROR', error)
    return []
  }
}

const arePoliciesEqual = (left: number[], right: number[]): boolean => {
  if (left.length !== right.length) {
    return false
  }

  const sortedLeft = [...left].sort((a, b) => a - b)
  const sortedRight = [...right].sort((a, b) => a - b)
  return sortedLeft.every((value, index) => value === sortedRight[index])
}

const stripTokenFields = (auth: AuthModel) => {
  const {token: _token, refreshToken: _refreshToken, refreshTokenExpiration: _exp, ...rest} = auth
  return rest
}

const isTokenOnlyAuthChange = (previous: AuthModel | undefined, next: AuthModel): boolean => {
  if (!previous) {
    return false
  }

  return JSON.stringify(stripTokenFields(previous)) === JSON.stringify(stripTokenFields(next))
}

export const mergeAuthState = (existing: AuthModel | undefined, incoming: AuthModel): AuthModel => {
  if (!existing) {
    return incoming
  }

  return {
    ...existing,
    ...incoming,
  }
}

/** Apply slim refresh result without wiping login profile (policies, etc.). */
export const mergeRefreshAuthState = (
  existing: AuthModel | undefined,
  incoming: RefreshTokenResponseModel
): AuthModel | null => {
  if (!existing) {
    return null
  }

  return {
    ...existing,
    isAuthenticated: incoming.isAuthenticated,
    message: incoming.message ?? existing.message,
    token: incoming.token,
    refreshToken: incoming.refreshToken,
    refreshTokenExpiration: incoming.refreshTokenExpiration,
  }
}

const updateAuthStateFromRefresh = (incoming: RefreshTokenResponseModel) => {
  const previousAuth = getAuth()
  const mergedAuth = mergeRefreshAuthState(previousAuth, incoming)
  if (!mergedAuth) {
    return null
  }

  setAuth(mergedAuth)
  // Token-only change — do not re-dispatch full auth-updated / policy reload.
  scheduleProactiveRefresh()
  return mergedAuth
}

const updateAuthState = (auth: AuthModel) => {
  const previousAuth = getAuth()
  const mergedAuth = mergeAuthState(previousAuth, auth)
  setAuth(mergedAuth)

  const previousPolicies = parsePolicies(previousAuth?.policies)
  const nextPolicies = parsePolicies(mergedAuth.policies)
  if (!arePoliciesEqual(previousPolicies, nextPolicies)) {
    getAuthSideEffects()?.setPolicies(nextPolicies)
  }

  if (!isTokenOnlyAuthChange(previousAuth, mergedAuth)) {
    window.dispatchEvent(new Event('auth-updated'))
  }

  scheduleProactiveRefresh()
}

export const clearSessionAndRedirect = () => {
  clearProactiveRefresh()
  removeAuth()
  getAuthSideEffects()?.resetState()

  if (isAuthRoutePath()) {
    return
  }

  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT))
}

const isNetworkError = (error: unknown): boolean => {
  const err = error as {response?: unknown}
  return !err?.response
}

const executeRefreshRequest = async (refreshTokenValue: string): Promise<AuthModel | null> => {
  try {
    const {data} = await refreshTokenRequest(refreshTokenValue)
    if (data?.isAuthenticated && data?.token && data?.refreshToken) {
      return updateAuthStateFromRefresh(data)
    }
    return null
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const {data} = await refreshTokenRequest(refreshTokenValue)
        if (data?.isAuthenticated && data?.token && data?.refreshToken) {
          return updateAuthStateFromRefresh(data)
        }
      } catch (retryError) {
        console.error('TOKEN REFRESH RETRY ERROR', retryError)
      }
    } else {
      console.error('TOKEN REFRESH ERROR', error)
    }
    return null
  }
}

export const requestTokenRefresh = async (): Promise<AuthModel | null> => {
  const auth = getAuth()

  if (!auth?.refreshToken) {
    return null
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const latestAuth = getAuth()
      if (!latestAuth?.refreshToken) {
        return null
      }

      return executeRefreshRequest(latestAuth.refreshToken)
    })().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

type JwtPayload = {
  exp?: number
}

export const isAccessTokenExpired = (
  token: string,
  bufferSeconds: number = DEFAULT_EXPIRY_BUFFER_SECONDS
): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token)
    if (!decoded.exp) {
      return false
    }

    const expiresAtMs = decoded.exp * 1000
    return Date.now() >= expiresAtMs - bufferSeconds * 1000
  } catch (error) {
    console.error('JWT DECODE ERROR', error)
    return false
  }
}

const getMsUntilRefresh = (
  token: string,
  bufferSeconds: number = DEFAULT_EXPIRY_BUFFER_SECONDS
): number | null => {
  try {
    const decoded = jwtDecode<JwtPayload>(token)
    if (!decoded.exp) {
      return null
    }

    const refreshAtMs = decoded.exp * 1000 - bufferSeconds * 1000
    return Math.max(refreshAtMs - Date.now(), 0)
  } catch (error) {
    console.error('JWT DECODE ERROR', error)
    return null
  }
}

export const clearProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer)
    proactiveRefreshTimer = null
  }
}

export const scheduleProactiveRefresh = () => {
  clearProactiveRefresh()

  const auth = getAuth()
  if (!auth?.token || !auth?.refreshToken) {
    return
  }

  const delayMs = getMsUntilRefresh(auth.token)
  if (delayMs === null) {
    return
  }

  proactiveRefreshTimer = setTimeout(async () => {
    await requestTokenRefresh()
  }, delayMs)
}

export const initAuthStorageSync = () => {
  if (storageSyncInitialized || typeof window === 'undefined') {
    return
  }

  storageSyncInitialized = true

  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_LOCAL_STORAGE_KEY) {
      return
    }

    if (event.newValue) {
      window.dispatchEvent(new Event('auth-updated'))
      scheduleProactiveRefresh()
      return
    }

    clearProactiveRefresh()
  })
}
